import { useState } from 'react';
import { Box, Text, HStack, VStack, Icon } from '@chakra-ui/react';
import { MdHelp, MdLogout, MdChevronRight, MdPerson, MdGroup, MdSettings } from 'react-icons/md';
import Sheet from '@/components/Sheet';
import SettingsSheet from '@/components/SettingsSheet';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';

interface ProfileSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ProfileSheet({ isOpen, onClose }: ProfileSheetProps) {
  const { user, logout } = useAuth();
  const [, navigate] = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const menuItems = [
    {
      icon: MdGroup,
      label: 'Household',
      action: () => { onClose(); navigate('/household'); },
    },
    {
      icon: MdHelp,
      label: 'Help',
      action: () => { onClose(); navigate('/ai-chef?from=help'); },
    },
    {
      icon: MdSettings,
      label: 'Settings',
      action: () => setSettingsOpen(true),
    },
    {
      icon: MdLogout,
      label: 'Sign out',
      action: () => { onClose(); logout(); },
      danger: true,
    },
  ];

  return (
    <>
      <SettingsSheet isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <Sheet isOpen={isOpen} onClose={onClose}>
        <Box px={5} pb={5}>
          {/* User info */}
          <HStack gap={3} py={4} mb={2} borderBottom="1px solid" borderColor="border.muted">
            <Box
              w="40px" h="40px"
              borderRadius="full"
              bg="bg.muted"
              border="1px solid"
              borderColor="border.muted"
              display="flex" alignItems="center" justifyContent="center"
              overflow="hidden"
              flexShrink={0}
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Icon as={MdPerson} boxSize={5} color="text.muted" />
              )}
            </Box>
            <Box minW={0}>
              {user?.name && (
                <Text fontFamily="heading" fontWeight="800" fontSize="14px" color="text.primary" lineClamp={1}>
                  {user.name}
                </Text>
              )}
              {user?.email && (
                <Text fontFamily="body" fontWeight="300" fontSize="12px" color="text.muted" lineClamp={1}>
                  {user.email}
                </Text>
              )}
            </Box>
          </HStack>

          {/* Menu items */}
          <VStack gap={2} align="stretch" mt={3}>
            {menuItems.map((item) => (
              <HStack
                key={item.label}
                gap={3}
                p={3}
                bg="bg.canvas"
                border="1px solid"
                borderColor="border.muted"
                borderRadius="12px"
                cursor="pointer"
                onClick={item.action}
                _hover={{ bg: 'bg.muted' }}
                transition="background 0.15s"
              >
                <Box
                  w="36px" h="36px"
                  borderRadius="9px"
                  bg={item.danger ? 'accent.subtle' : 'bg.muted'}
                  display="flex" alignItems="center" justifyContent="center"
                  flexShrink={0}
                >
                  <Icon
                    as={item.icon}
                    boxSize={4.5}
                    color={item.danger ? 'accent.emphasis' : 'text.muted'}
                  />
                </Box>
                <Text
                  fontFamily="body"
                  fontWeight="500"
                  fontSize="13px"
                  color={item.danger ? 'accent.emphasis' : 'text.primary'}
                  flex={1}
                >
                  {item.label}
                </Text>
                <Icon as={MdChevronRight} boxSize={4} color="text.muted" flexShrink={0} />
              </HStack>
            ))}
          </VStack>
        </Box>
      </Sheet>
    </>
  );
}
